import { inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { AbsenceModel } from '@reducers/orm/absence/absence.model';
import { AvailabilityModel } from '@reducers/orm/availability/availability.model';
import { EventModel } from '@reducers/orm/event/event.model';
import { ScheduleTargetInsightsStore } from '@reducers/orm/insights/insights-schedule.store';
import { OpenShiftModel } from '@reducers/orm/open-shift/open-shift.model';
import { RequiredShiftModel } from '@reducers/orm/required-shift/required-shift.model';
import { ScheduleAction } from '@reducers/orm/schedule/schedule.action';
import { ScheduleModel } from '@reducers/orm/schedule/schedule.model';
import { ScheduleFilterPeriod } from '@reducers/page-filters/page-filters.model';
import { getLoanedShiftsFilter } from '@reducers/page-filters/page-filters.service';
import { getSelectedDepartmentIds } from '@reducers/selected-departments/selected-departments.service';
import { getArrayFromActionPayload, PayloadKeyType } from '@reducers/shared/entity.helper';
import { ScheduleNAction } from '@reducers/store/schedule/schedule.n.action';
import { parseDate } from '@shiftbase-com/ts-shared/utilities';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of, type ObservedValueOf } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { groupDepartmentData } from '../../../../shared/schedule/tasks/group-department-data';
import { groupEmployeeData } from '../../../../shared/schedule/tasks/group-rosters';
import { groupTeamData } from '../../../../shared/schedule/tasks/group-team-data';
import { getDayListWithoutTotals } from '../../employee/schedule-employee-period/schedule-employee-period.helper';
import { ScheduleHelperService } from '../schedule-helper.service';
import { combineScheduleDatasets } from './combine-schedule-datasets';

type FetchedScheduleData = NonNullable<ObservedValueOf<ReturnType<NPeriodService['fetchSchedules']>>>;

@Injectable({ providedIn: 'root' })
export class NPeriodService {
  private scheduleHelperService = inject(ScheduleHelperService);
  private store = inject<Store<AppState>>(Store);
  private insightsStore = inject(ScheduleTargetInsightsStore);

  private period: ScheduleFilterPeriod;

  #loadedDepartmentIds = signal<string[] | undefined>(undefined);

  #selectedDepartmentIds = this.store.selectSignal(getSelectedDepartmentIds);

  isLoading = new BehaviorSubject<boolean>(true);

  reload(): Observable<void> {
    const period = this.getPeriod();
    if (!period) return EMPTY;
    this.scheduleHelperService.loadCompliances(period.minDate, period.maxDate);
    return this.fetchSchedules(period).pipe(
      tap((scheduleDataSet) => {
        if (!scheduleDataSet) return;
        this.processData(scheduleDataSet);
        this.insightsStore.loadInsights();
      }),
      map(() => undefined),
    );
  }

  fetchSchedules(schedulePeriod: ScheduleFilterPeriod) {
    return combineLatest([
      of(schedulePeriod),
      this.store.select(getSelectedDepartmentIds),
      this.store.select(getLoanedShiftsFilter),
    ]).pipe(
      switchMap(([period, selectedDepartmentIds, loanedShiftsFilter]) =>
        this.scheduleHelperService
          .loadDataObs(period.minDate, period.maxDate, selectedDepartmentIds, loanedShiftsFilter)
          .pipe(
            map((scheduleDataSet) => ({ scheduleDataSet, departmentIds: selectedDepartmentIds })),
            // A failed refetch would otherwise hold the gated rows forever; fall
            // back to showing the loaded data filtered to the new selection.
            tap({ error: () => this.#loadedDepartmentIds.set(selectedDepartmentIds) }),
          ),
      ),
      map(({ scheduleDataSet, departmentIds }) => {
        if (!scheduleDataSet) {
          return null;
        }

        const totals = getDayListWithoutTotals({
          start: parseDate(this.period.minDate),
          end: parseDate(this.period.maxDate),
        });

        const dataSet = combineScheduleDatasets(scheduleDataSet);

        const schedules = getArrayFromActionPayload<ScheduleModel>(PayloadKeyType.SCHEDULES, dataSet.entities);
        const requiredShifts = getArrayFromActionPayload<RequiredShiftModel>(
          PayloadKeyType.REQUIRED_SHIFTS,
          dataSet.entities,
        );
        const absences = getArrayFromActionPayload<AbsenceModel>(PayloadKeyType.ABSENCE, dataSet.entities);
        const employeeData = {
          schedules,
          absences,
          availabilities: getArrayFromActionPayload<AvailabilityModel>(PayloadKeyType.AVAILABILITIES, dataSet.entities),
        };
        const teamData = {
          openShifts: getArrayFromActionPayload<OpenShiftModel>(PayloadKeyType.OPENSHIFTS, dataSet.entities),
          requiredShifts,
          schedules,
        };

        const departmentData = {
          schedules,
          events: getArrayFromActionPayload<EventModel>(PayloadKeyType.EVENTS, dataSet.entities),
          requiredShifts,
          absences,
        };

        return {
          dataSet,
          employeeData,
          teamData,
          departmentData,
          totals,
          departmentIds,
        };
      }),
    );
  }

  processData({ dataSet, employeeData, teamData, departmentData, totals, departmentIds }: FetchedScheduleData) {
    // Recorded before the dispatch: the grid gates its render on it, and
    // reacts synchronously to the load success.
    this.#loadedDepartmentIds.set(departmentIds);

    this.store.dispatch(
      ScheduleNAction.loadSuccess(
        {
          scheduleData: groupEmployeeData(employeeData, totals),
          teamData: groupTeamData(teamData, totals),
          departmentData: groupDepartmentData(departmentData, totals),
        },
        {
          minDate: this.period.minDate,
          maxDate: this.period.maxDate,
        },
      ),
    );

    this.store.dispatch(ScheduleAction.loadScheduleDatasetSuccess(dataSet, this.period));
    this.isLoading.next(false);
  }

  /**
   * Whether the schedule data in the store was loaded for the current
   * department selection. A department change re-filters the loaded data
   * before the refetch for the new selection lands, so a render can wait on
   * this. Reads the selection as a signal: it is already current when a
   * `store.select()` subscriber is notified of the change.
   *
   * Gate only a subscription's later values on this, never its first: a row
   * the virtual scroller mounts mid-refetch has no earlier render to keep.
   */
  isSelectionLoaded(): boolean {
    const loadedDepartmentIds = this.#loadedDepartmentIds();
    const selectedDepartmentIds = this.#selectedDepartmentIds();

    if (!loadedDepartmentIds || loadedDepartmentIds.length !== selectedDepartmentIds.length) {
      return false;
    }

    const loaded = new Set(loadedDepartmentIds);
    return selectedDepartmentIds.every((departmentId) => loaded.has(departmentId));
  }

  setPeriod(period: ScheduleFilterPeriod) {
    this.period = period;
    if (!period) {
      this.isLoading.next(false);
    }
  }

  getPeriod() {
    return this.period;
  }
}
